// Shared card for both top-recommended-actions (from weeklyLens) and
// per-signal action backlog. Reads camelCase fields with PascalCase fallback.

import React from "react";
import { hasContent } from "../../../lib/clients";

const Badge = ({ tone = "muted", children }) => (
    <span className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`}>
        {children}
    </span>
);

const PRIORITY_TONE = { HIGH: "negative", MEDIUM: "warning", LOW: "muted" };

const RecommendedActionCard = ({ action, onSelectTrack, trackCardsById }) => {
    if (!action) return null;
    const title = action.actionTitle || action.action;
    if (!hasContent(title)) return null;

    const priority = (action.priority || "").toUpperCase();
    const owner = action.ownerSuggestion || action.owner;
    const timeframe = action.timeHorizon || action.timeframe;
    const rationale = action.rationale;
    const firstStep = action.firstStep;
    const related = Array.isArray(action.relatedTrackIds) ? action.relatedTrackIds : [];

    return (
        <article className="card-sunken p-3.5" data-testid={`recommended-action-${action.actionId || title}`}>
            <header className="flex flex-wrap items-center gap-1.5 mb-1.5">
                {typeof action.rank === "number" && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold num text-[hsl(var(--mute))]">#{action.rank}</span>
                )}
                {priority && <Badge tone={PRIORITY_TONE[priority] || "muted"}>{priority}</Badge>}
                {action.actionType && <Badge tone="muted">{action.actionType}</Badge>}
                {timeframe && <Badge tone="muted">{timeframe}</Badge>}
            </header>
            <h4 className="text-sm font-semibold text-[hsl(var(--ink))] leading-snug">{title}</h4>
            {hasContent(rationale) && (
                <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-2">{rationale}</p>
            )}
            <dl className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
                {hasContent(owner) && (
                    <div>
                        <dt className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium">Owner</dt>
                        <dd className="text-[hsl(var(--ink-soft))]">{owner}</dd>
                    </div>
                )}
                {hasContent(firstStep) && (
                    <div>
                        <dt className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium">First step</dt>
                        <dd className="text-[hsl(var(--ink-soft))]">{firstStep}</dd>
                    </div>
                )}
                {hasContent(action.successMetric) && (
                    <div>
                        <dt className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium">Success metric</dt>
                        <dd className="text-[hsl(var(--ink-soft))]">{action.successMetric}</dd>
                    </div>
                )}
                {hasContent(action.budgetIndication) && (
                    <div>
                        <dt className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium">Budget</dt>
                        <dd className="text-[hsl(var(--ink-soft))]">{action.budgetIndication}</dd>
                    </div>
                )}
            </dl>
            {hasContent(related) && onSelectTrack && (
                <div className="mt-2.5 flex flex-wrap gap-1.5 items-center">
                    <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Related signals</span>
                    {related.map((tid) => {
                        const name = trackCardsById?.[tid]?.stableTrackName || tid;
                        return (
                            <button
                                key={tid}
                                type="button"
                                onClick={() => onSelectTrack(tid)}
                                data-testid={`related-track-${tid}`}
                                className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[hsl(var(--surface-raised))] border border-[hsl(var(--line))] text-[hsl(var(--ink-soft))] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent-ink))]"
                                title={tid}
                            >
                                {name}
                            </button>
                        );
                    })}
                </div>
            )}
        </article>
    );
};

export default RecommendedActionCard;
